import { ReactNode } from "react";
import { colors } from "../../stitches.config";
import { BottomBannerWrapper } from "../Maps/__styles__/FullPageMap";
import { usedActivationKey } from "../Maps/MapTools/MeasureTool/utils";
import {
  Container,
  Header,
  InfoContainer,
  PrimaryContent,
  StyledIcon,
  TitleTextWrapper,
} from "./__styles__/BottomBanner";
import { ActionButton } from "./Buttons/ActionButton";
import { Icons } from "./Icons/LucideIcons";
import { Body } from "./Typography";

type BottomBannerStyles = "default" | "alert" | "warning" | "table";

const styleConstants: Record<
  BottomBannerStyles,
  {
    color: keyof typeof colors;
    iconName?: keyof typeof Icons;
    iconColor?: keyof typeof colors;
    iconFill?: string;
    shouldTruncate?: boolean;
  }
> = {
  alert: {
    color: "contentCriticalInverted",
    iconName: "alert-circle",
  },
  warning: { color: "contentAttention", iconName: "triangle-alert" },
  default: { color: "contentPrimaryInverted" },
  table: {
    color: "contentPrimaryInverted",
    shouldTruncate: true,
  },
};

export const BottomBanner = ({
  title,
  subtitle,
  children,
  onClose,
  style = "default",
  infoPanelOpen = false,
  tooltipComponent,
}: {
  title: ReactNode | string;
  subtitle?: string;
  children: ReactNode;
  onClose: () => void;
  style?: BottomBannerStyles;
  infoPanelOpen?: boolean;
  tooltipComponent?: ReactNode;
}) => {
  const { color, iconName, iconFill, iconColor, shouldTruncate } =
    styleConstants[style];

  return (
    <BottomBannerWrapper infoPanelOpen={infoPanelOpen}>
      {tooltipComponent}
      <Container>
        {iconName && (
          <StyledIcon
            iconName={iconName}
            color={iconColor ?? color}
            size="16"
            fill={iconFill}
          />
        )}
        <PrimaryContent>
          <Header>
            {typeof title === "string" ? (
              <div>
                <Body size="default" type="emphasis" color={color}>
                  <TitleTextWrapper shouldTruncate={shouldTruncate}>
                    {title}
                  </TitleTextWrapper>
                </Body>
              </div>
            ) : (
              title
            )}
          </Header>
          <InfoContainer hasNoSubtitle={!subtitle}>
            {subtitle && (
              <Body
                type="regular"
                size="small"
                color="contentSecondaryInverted"
              >
                {subtitle}
              </Body>
            )}
            {children}
          </InfoContainer>
        </PrimaryContent>
        <div>
          <ActionButton
            icon="x"
            variant="inverted"
            onClick={onClose}
            onKeyDown={e => {
              if (usedActivationKey(e)) {
                e.preventDefault();
                onClose();
              }
            }}
            data-testid="close-icon"
            aria-label="Close Banner"
          />
        </div>
      </Container>
    </BottomBannerWrapper>
  );
};
